import React from 'react';
import PropTypes from 'prop-types';
import { Button as MuiButton, styled } from '@mui/material';

// Styled Button component
const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'variant' || prop !== 'color',
})
(({ theme, color = 'primary', variant = 'contained' }) => ({
  borderRadius: '12px',
  transition: 'all 200ms ease',
  textTransform: 'none',
  fontWeight: 500,

  // Primary gradient style for contained variant
  ...(variant === 'contained' &&
    color === 'primary' && {
      background:
        'linear-gradient(90deg, rgb(12, 181, 247) 0%, rgb(152, 87, 199) 80%)',
      '&:hover': {
        background:
          'linear-gradient(90deg, rgb(10, 157, 216) 0%, rgb(121, 56, 168) 80%)',
        boxShadow: '0 5px 15px rgba(12, 181, 247, 0.3)',
      },
    }),

  // Variant: neutral (кастомная логика через outlined)
  ...(variant === 'neutral' && {
    backgroundColor: theme.palette.mode === 'light' ? '#f7fafc' : '#2d3748',
    color: theme.palette.text.primary,
    border: `1px solid ${
      theme.palette.mode === 'light' ? '#e2e8f0' : '#4a5568'
    }`,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? '#edf2f7' : '#4a5568',
    },
  }),

  // Size-specific styles
  ...(theme.props?.MuiButton?.size === 'small' && {
    padding: '6px 16px',
    fontSize: '0.8125rem',
  }),
  ...(theme.props?.MuiButton?.size === 'medium' && {
    padding: '8px 20px',
    fontSize: '0.875rem',
  }),
  ...(theme.props?.MuiButton?.size === 'large' && {
    padding: '10px 22px',
    fontSize: '0.9375rem',
  }),

  // Focus state
  '&:focus-visible': {
    boxShadow: `0 0 0 3px ${
      theme.palette[color]?.light || theme.palette.primary.light
    }40`,
  },

  // Стили для outlined варианта
  ...(variant === 'outlined' && {
    borderColor: theme.palette[color]?.main || theme.palette.primary.main,
    color: theme.palette[color]?.main || theme.palette.primary.main,
    '&:hover': {
      borderColor: theme.palette[color]?.dark || theme.palette.primary.dark,
      background: `${theme.palette[color]?.main || theme.palette.primary.main}0a`,
    },
  }),

  // Стили для text варианта
  ...(variant === 'text' && {
    color: theme.palette[color]?.main || theme.palette.primary.main,
    '&:hover': {
      background: `${theme.palette[color]?.main || theme.palette.primary.main}0a`,
    },
  }),

  // Custom color handling for non-neutral variants and non-primary colors
  ...(color !== 'primary' &&
    variant === 'contained' && {
      backgroundColor: theme.palette[color]?.main || theme.palette.primary.main,
      color: theme.palette[color]?.contrastText || theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette[color]?.dark || theme.palette.primary.dark,
        boxShadow: `0 5px 15px ${theme.palette[color]?.main || theme.palette.primary.main}33`,
      },
    }),

  // Disabled state
  '&.Mui-disabled': {
    backgroundImage: 'none',
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

// Button component
const Button = ({
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  children,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      color={color}
      size={size}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

Button.propTypes = {
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text', 'neutral']),
  /**
   * The color to use.
   */
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'error',
    'info',
    'warning',
  ]),
  /**
   * The size of the button.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   * If true, the button will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * The content of the button.
   */
  children: PropTypes.node,
};

export default Button;
